import { some, every, keyBy, intersection } from 'lodash';
import { getSelectedAdmissionItem, getRegistrationTypeId,
  allSelectedSessions } from '../../../utils/eventRegistration';
import buildValidators from '../../../utils/validation/buildValidators';
import { defaultMemoize, createSelector } from 'reselect';

const simplify = defaultMemoize(rule => ({
  min: rule.minSessionAllowed || 0,
  max: rule.maxSessionAllowed || Infinity
}));

const ruleHasMinMax = createSelector(
  simplify,
  minMax => minMax.min > 0 || minMax.max < Infinity
);

const ruleHasAssociatedSessions = defaultMemoize(rule =>
  rule.optionalSession && rule.optionalSession.length > 0);

const sessionAdvancedRuleIsValid = (rule, selectedSessions) => {
  const selectedSessionIds = selectedSessions.map(session => session.productId);

  const numberSelectedRuleSessions = intersection(rule.optionalSession, selectedSessionIds)
    .length;
  const minMax = simplify(rule);

  return numberSelectedRuleSessions >= minMax.min && numberSelectedRuleSessions <= minMax.max;
};

const sessionAdvancedRuleIsValidForRegistrant = (rule, eventRegistration) => {
  // Now calculates all the sessions selected including the ones included by admission Item.
  const selectedSessions = allSelectedSessions(eventRegistration);
  return sessionAdvancedRuleIsValid(rule, selectedSessions);
};

const ruleApplies = (rule, registrationTypeId, admissionItem) =>
  some(rule.contactType, ruleRegistrationTypeId => registrationTypeId === ruleRegistrationTypeId) &&
  some(rule.admissionItem, ruleAdmissionItemId => admissionItem.productId === ruleAdmissionItemId);

const ruleAppliesToRegistrant = (rule, eventRegistration) => {
  if (!ruleHasAssociatedSessions(rule) || !ruleHasMinMax(rule)) {
    return false;
  }

  const admissionItem = getSelectedAdmissionItem(eventRegistration);
  if (!admissionItem) {
    return false;
  }

  const registrationTypeId = getRegistrationTypeId(eventRegistration);

  return ruleApplies(rule, registrationTypeId, admissionItem);
};

const getRuleWarningMessage = (translate, rule) => translate(rule.warningMessage);

const builder = defaultMemoize(buildValidators({
  objectValidatesRegistrant: ruleAppliesToRegistrant,
  registrantIsValid: sessionAdvancedRuleIsValidForRegistrant,
  getValidationMessage: getRuleWarningMessage
}));

export const speculativelyValidateSessionAdvancedRules = (
  rules,
  registrationTypeId,
  admissionItem,
  selectedSessions) => {
  const applicableRules = rules.filter(rule => ruleApplies(rule, registrationTypeId, admissionItem));

  return every(applicableRules, rule => sessionAdvancedRuleIsValid(rule, selectedSessions));
};

export default (args) => {
  const { advancedSessionRules } = args;

  const rulesById = keyBy(advancedSessionRules, 'id');

  return builder({
    ...args,
    validationObjects: rulesById
  });
};
